import {keepPreviousData, useQuery} from '@tanstack/react-query';
import {loggedInEmployeeAtom} from 'atoms/employee';
import {useAtomValue} from 'jotai';
import {useMemo} from 'react';
import {getEmployeeService} from 'utilities';

import {CatalogApp} from './catalogApp';
import {getCatalogAppAuthorLdap, normalizeCatalogValue} from './catalogFilters';
import {getOrgLabel} from './catalogOrgs';

/**
 * Resolves every catalog author's display name and Workday org in one batch request,
 * along with the viewer's own org.
 */
export const useCatalogAuthorDirectory = ({
  apps,
  enabled,
}: {
  apps: readonly CatalogApp[];
  enabled: boolean;
}) => {
  const viewerLdap = useAtomValue(loggedInEmployeeAtom).employee?.ldap;
  const normalizedViewerLdap = viewerLdap ? normalizeCatalogValue(viewerLdap) : null;

  const ldaps = useMemo(() => {
    const unique = new Set<string>();
    for (const app of apps) {
      const ldap = getCatalogAppAuthorLdap(app);
      if (ldap) unique.add(ldap);
    }
    if (normalizedViewerLdap) unique.add(normalizedViewerLdap);
    return [...unique].sort();
  }, [apps, normalizedViewerLdap]);

  const {
    data: employees,
    isError,
    isLoading,
  } = useQuery({
    queryKey: ['apps', 'catalog-authors', ldaps],
    queryFn: () => getEmployeeService().getEmployeesByLdapsApiV1PeopleLdapsPost(ldaps),
    enabled: enabled && ldaps.length > 0,
    placeholderData: keepPreviousData,
    staleTime: 5 * 60 * 1000,
  });

  return useMemo(() => {
    const nameByLdap = new Map<string, string>();
    const orgByLdap = new Map<string, string>();
    for (const employee of employees ?? []) {
      const ldap = normalizeCatalogValue(employee.ldap);
      if (employee.name) nameByLdap.set(ldap, employee.name);
      const org = getOrgLabel(employee.team);
      if (org) orgByLdap.set(ldap, org);
    }
    return {
      isError,
      isLoading,
      nameByLdap,
      orgByLdap,
      viewerOrg: (normalizedViewerLdap && orgByLdap.get(normalizedViewerLdap)) || null,
    };
  }, [employees, isError, isLoading, normalizedViewerLdap]);
};
